/**
 * Self Improvement Loop — the Revision Engine abstraction.
 *
 * A RevisionEngine is anything with `revise(inputs)` returning a RevisionResult
 * (or a promise of one). The loop awaits the call, so a synchronous engine and
 * a future networked engine satisfy the same contract.
 *
 * This is the seam for OpenAI. Later, `createOpenAIRevisionEngine()` can
 * implement `revise` by asking a model to improve the Creative Plan, and the
 * loop, the inputs, and the public API stay exactly as they are today.
 *
 * @typedef {Object} RevisionInputs
 * @property {Object} plan
 * @property {Object} review
 * @property {Object} research
 * @property {Object} genome
 * @property {Object[]} patterns
 * @property {Object} [constraints]
 *
 * @typedef {Object} RevisionResult
 * @property {Object} plan               the improved Creative Plan.
 * @property {Object[]} patterns         the improved pattern selection.
 * @property {string} revisionSummary
 * @property {Object[]} changedDecisions
 * @property {string[]} reasoning
 * @property {number} confidenceDelta
 * @property {Object[]} [unaddressed]
 *
 * @typedef {Object} RevisionEngine
 * @property {string} id
 * @property {(inputs: RevisionInputs) => (RevisionResult|Promise<RevisionResult>)} revise
 */

import { reviseCreativePlan } from './revise';

/**
 * The deterministic Revision Engine: a senior designer applying the notes,
 * with no model in the loop.
 *
 * @returns {RevisionEngine}
 */
export const createDeterministicRevisionEngine = () => ({
  id: 'deterministic/1',
  revise: (inputs) => reviseCreativePlan(inputs),
});

/** The engine the loop uses unless another is injected. */
export const defaultRevisionEngine = createDeterministicRevisionEngine();

/** True when `engine` satisfies the RevisionEngine contract. */
export const isRevisionEngine = (engine) =>
  Boolean(engine) && typeof engine === 'object' && typeof engine.revise === 'function';
